<svelte:options accessors={true} />

<script lang="ts">
	import { Heading } from "flowbite-svelte-v1";
	import { afterUpdate } from "svelte";
	import { twMerge } from "tailwind-merge";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import { providerIcons } from "#lib/iconProvider.js";
	import type { SubstitutionToken } from "#lib/tokenProvider.js";
	import type { SuggestionProps } from "@tiptap/suggestion";

	export let props: SuggestionProps;

	let activeIndex = 0;
	let listElement: HTMLUListElement;

	$: suggestions = props.items as SubstitutionToken[];

	afterUpdate(() => {
		if (!listElement) {
			return;
		}
		const item = listElement.children[activeIndex];
		if (item) {
			item.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
		}
	});

	export function onKeyDown({ event }: { event: KeyboardEvent }) {
		if (event.key === "ArrowUp") {
			upHandler();
			return true;
		}
		if (event.key === "ArrowDown") {
			downHandler();
			return true;
		}
		if (event.key === "Enter") {
			return enterHandler();
		}
		return false;
	}

	function upHandler() {
		activeIndex = (activeIndex + suggestions.length - 1) % suggestions.length;
	}

	function downHandler() {
		activeIndex = (activeIndex + 1) % suggestions.length;
	}

	function enterHandler(): boolean {
		if (suggestions.length) {
			onSelect(activeIndex);
			return true;
		}
		return false;
	}

	function onSelect(idx: number) {
		const item = suggestions[idx];
		if (item) {
			props.command(item);
		}
	}
</script>

{#if suggestions.length}
	<ul bind:this={listElement} class="focus-visible:outline-primary-500 mt-1 w-full overflow-hidden rounded-lg border border-gray-300 bg-white text-gray-700 shadow-md transition-[max-height_visibility] duration-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
		{#each suggestions as suggestion, index (index)}
			{@const icon = providerIcons.get(suggestion.provider ?? "") ?? undefined}
			<li>
				<button type="button" role="option" aria-selected={activeIndex === index} tabindex="-1" on:mouseenter={() => (activeIndex = index)} on:click={() => onSelect(index)} class={twMerge("w-full cursor-pointer items-center gap-2 p-4 font-medium", index === activeIndex && "bg-gray-100 dark:bg-gray-600")}>
					<div class="flex items-center gap-3">
						<Icon iconSet={icon?.iconSet} name={icon?.icon} class="h-5 w-5 flex-none" />
						<Heading tag="h4" class="w-48 flex-none text-left text-sm font-medium text-gray-900">{suggestion.label}</Heading>
						<P class="grow" size="sm" secondary>{suggestion.description ?? ""}</P>
					</div>
				</button>
			</li>
		{/each}
	</ul>
{/if}
